// src/components/Admin/VerificaPersonale.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy 
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getLocationName } from '../../utils/locationUtils';
import './VerificaPersonale.css';

const VerificaPersonale = () => {
  // Stati principali
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timbrature, setTimbrature] = useState([]);
  const [rapportini, setRapportini] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [users, setUsers] = useState({});
  const [workHours, setWorkHours] = useState([]);  // ✅ Ore manuali
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCantiere, setSelectedCantiere] = useState(null);
  const [personaleDetails, setPersonaleDetails] = useState([]);  // ✅ Dettagli personale
  
  // Statistiche per cantiere
  const [cantieriStats, setCantieriStats] = useState([]);
  
  // 🔍 Ricerca persona
  const [searchQuery, setSearchQuery] = useState('');
  
  // Notifiche
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Carica dati iniziali
  useEffect(() => {
    loadAllData();
  }, [selectedDate]);

  // ========================================
  // FUNZIONI DI CARICAMENTO DATI
  // ========================================
  
  const loadAllData = async () => {
    setIsLoading(true);
    try {
      // Carica tutti i dati in parallelo
      const [usersData, cantieriData] = await Promise.all([
        loadUsers(),
        loadCantieri()
      ]);
      
      setUsers(usersData);
      setCantieri(cantieriData);
      
      // Carica timbrature e rapportini per la data selezionata
      await loadDataForDate(formatDate(selectedDate));
      
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      showNotification('Errore nel caricamento dei dati', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      const usersMap = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        usersMap[doc.id] = {
          id: doc.id,
          nome: data.nome || '',
          cognome: data.cognome || '',
          email: data.email || '',
          displayName: `${data.nome || ''} ${data.cognome || ''}`.trim() || data.email
        };
      });
      
      return usersMap;
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
      return {};
    }
  };

  const loadCantieri = async () => {
    try {
      const cantieriRef = collection(db, 'cantieri');
      const q = query(
        cantieriRef,
        where('stato', '==', 'Attivo')
      );
      
      const snapshot = await getDocs(q);
      const cantieriList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Aggiungi "Officina Pistavecchia" come location speciale
      cantieriList.unshift({
        id: 'officina-pistavecchia',
        nomeCommessa: 'Officina Pistavecchia',
        isOfficina: true
      });
      
      return cantieriList;
    } catch (error) {
      console.error('Errore caricamento cantieri:', error);
      return [];
    }
  };

  const loadDataForDate = async (dateString) => {
    try {
      // ✅ Carica timbrature badge da collection 'timekeeping'
      // NOTA: Queste sono timbrature fatte con badge/QR da kiosk
      //       NON hanno clockInLocation (usano scanInfo.clockIn.deviceId)
      const timbratureRef = collection(db, 'timekeeping');
      const timbratureQuery = query(
        timbratureRef,
        where('date', '==', dateString)
      );
      
      const timbratureSnapshot = await getDocs(timbratureQuery);
      const timbratureData = timbratureSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTimbrature(timbratureData);
      
      console.log(`[DEBUG] Timbrature badge caricate per ${dateString}:`, timbratureData.length);
      
// ✅ Carica rapportini CANTIERI usando nomeRapportino (formato "AAAA-MM-DD")
const rapportiniRef = collection(db, 'rapportini');
const rapportiniQuery = query(
  rapportiniRef,
  where('nomeRapportino', '==', dateString),  // ✅ String "2025-10-21"
  where('stato', '==', 'Attivo')
);

let rapportiniSnapshot = await getDocs(rapportiniQuery);

// ⚠️ FALLBACK: Se non trova rapportini con nomeRapportino, prova con data timestamp
// Questo serve per rapportini automatici vecchi che non hanno nomeRapportino
if (rapportiniSnapshot.empty) {
  console.log(`[DEBUG] Nessun rapportino trovato con nomeRapportino, provo con data timestamp...`);
  
  const [year, month, day] = dateString.split('-').map(Number);
  const searchDate = new Date(year, month - 1, day, 1, 0, 0);  // 01:00:00 per evitare timezone issues
  
  const rapportiniQueryFallback = query(
    rapportiniRef,
    where('data', '==', searchDate),
    where('stato', '==', 'Attivo')
  );
  
  rapportiniSnapshot = await getDocs(rapportiniQueryFallback);
  console.log(`[DEBUG] Rapportini trovati con data timestamp:`, rapportiniSnapshot.size);
}

const rapportiniCantieriData = rapportiniSnapshot.docs.map(doc => ({
  id: doc.id,
  tipo: 'cantiere',  // ✅ Marker per distinguere tipo
  ...doc.data()
}));

console.log(`[DEBUG] Rapportini cantieri caricati:`, rapportiniCantieriData.length);
      
      // ✅ Carica rapportini OFFICINA per Meccanico/Capo Officina
      // Query con campo "data" Timestamp invece di "nomeRapportino" string
      const rapportiniOfficinaRef = collection(db, 'rapportiniOfficina');
      
      // Converti dateString in range timestamp (inizio e fine giornata)
      const [year, month, day] = dateString.split('-').map(Number);
      const startOfDay = new Date(year, month - 1, day, 0, 0, 0);  // 00:00:00
      const endOfDay = new Date(year, month - 1, day, 23, 59, 59);  // 23:59:59
      
      const rapportiniOfficinaQuery = query(
        rapportiniOfficinaRef,
        where('data', '>=', startOfDay),
        where('data', '<=', endOfDay)
        // ✅ Rimosso filtro 'stato' per evitare indice composito
      );
      
      const rapportiniOfficinaSnapshot = await getDocs(rapportiniOfficinaQuery);
      const rapportiniOfficinaData = rapportiniOfficinaSnapshot.docs
        .filter(doc => doc.data().stato === 'Attivo')  // ✅ Filtro in memoria
        .map(doc => ({
          id: doc.id,
          tipo: 'officina',  // ✅ Marker per distinguere tipo
          cantiereId: 'officina-pistavecchia',  // ✅ ID sintetico per officina
          ...doc.data()
        }));
      
      console.log(`[DEBUG] Rapportini officina caricati:`, rapportiniOfficinaData.length);
      
      // ✅ Combina entrambi i tipi di rapportini
      const rapportiniData = [...rapportiniCantieriData, ...rapportiniOfficinaData];
      
      setRapportini(rapportiniData);
      
      console.log(`[DEBUG] Rapportini totali (cantieri + officina):`, rapportiniData.length);
      
      // ✅ Carica ore manuali (workHours) - STRUTTURA CON ENTRIES ARRAY
      const workHoursRef = collection(db, 'workHours');
      
      // Estrai anno e mese dalla data (come STRING per workHours)
      const [yearWH, monthWH] = dateString.split('-');
      
      // Query per anno e mese (non possiamo filtrare per date dentro entries)
      const workHoursQuery = query(
        workHoursRef,
        where('year', '==', yearWH),
        where('month', '==', monthWH)
      );
      
      const workHoursSnapshot = await getDocs(workHoursQuery);
      
      // Filtra in memoria per trovare entries con la data specifica
      const workHoursForDate = [];
      
      workHoursSnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Cerca entries con la data specifica
        if (data.entries && Array.isArray(data.entries)) {
          data.entries.forEach(entry => {
            if (entry.date === dateString) {
              // Aggiungi questo entry alla lista
              workHoursForDate.push({
                id: doc.id,
                userId: data.userId,
                date: entry.date,
                total: entry.total || 0,
                overtime: entry.overtime || 0,
                notes: entry.notes,
                correctedByReport: entry.correctedByReport
              });
            }
          });
        }
      });
      
      setWorkHours(workHoursForDate);
      
      console.log(`[DEBUG] WorkHours trovati per ${dateString}:`, workHoursForDate.length);
      console.log('[DEBUG] WorkHours data:', workHoursForDate);
      if (workHoursForDate.length > 0) {
        console.log('[DEBUG] Primo workHour:', workHoursForDate[0]);
        console.log('[DEBUG] Campi disponibili:', Object.keys(workHoursForDate[0]));
      }
      
      // ✅ Calcola statistiche per cantiere (ora è async)
      await calculateCantieriStats(timbratureData, rapportiniData, workHoursForDate);
      
    } catch (error) {
      console.error('Errore caricamento dati per data:', error);
    }
  };

  // ========================================
  // CALCOLO STATISTICHE
  // ========================================
  
  const calculateCantieriStats = async (timbratureData, rapportiniData, workHoursData) => {
    const stats = {};
    
    // Raccogli timbrature per posizione
    timbratureData.forEach(timbratura => {
      // Considera solo ingressi (hanno posizione)
      if (timbratura.status === 'in-progress' || timbratura.status === 'completed' || timbratura.status === 'auto-closed') {
        const location = timbratura.clockInLocation || 'non-specificata';
        
        if (!stats[location]) {
          stats[location] = {
            location: location,
            personalePresente: new Set(),
            anomalie: []
          };
        }
        
        stats[location].personalePresente.add(timbratura.userId);
      }
    });
    
    // Raccogli personale dai rapportini
    rapportiniData.forEach(rapportino => {
      const cantiereId = rapportino.cantiereId;
      
      if (!stats[cantiereId]) {
        stats[cantiereId] = {
          location: cantiereId,
          personalePresente: new Set(),
          anomalie: []
        };
      }
      
      // Aggiungi tutto il personale presente nel rapportino
      if (rapportino.personalePresente && Array.isArray(rapportino.personalePresente)) {
        rapportino.personalePresente.forEach(persona => {
          // ✅ Cerca tutti i possibili campi ID
          const userId = persona.personaId || persona.userId || persona.id || persona.uid;
          if (userId) {
            stats[cantiereId].personalePresente.add(userId);
          }
        });
      }
    });
    
    // ℹ️ NON aggiungiamo workHours a stats perché:
    // 1. workHours non contiene cantiereId
    // 2. Se persona è nel rapportino, è già stata aggiunta
    // 3. workHours serve solo per mostrare badge "Manuale" invece di "Mancante"
    
    // ✅ Converti in array e recupera nomi posizioni + responsabili
    const statsArray = await Promise.all(
      Object.keys(stats).map(async (locationId) => {
        let locationName;
        let responsabili = [];
        
        try {
          // Usa getLocationName per ottenere il nome corretto
          locationName = await getLocationName(locationId);
        } catch (error) {
          console.error(`Errore recupero nome per ${locationId}:`, error);
          // Fallback: cerca nei cantieri caricati
          const cantiere = cantieri.find(c => c.id === locationId);
          locationName = cantiere ? cantiere.nomeCommessa : locationId;
        }
        
        // ✅ Recupera responsabili del cantiere dal campo responsabiliCantiere
        const cantiere = cantieri.find(c => c.id === locationId);
        
        if (cantiere && cantiere.responsabiliCantiere) {
          // responsabiliCantiere può essere un array di userId o un singolo userId
          const respIds = Array.isArray(cantiere.responsabiliCantiere) 
            ? cantiere.responsabiliCantiere 
            : [cantiere.responsabiliCantiere];
          
          respIds.forEach(respId => {
            const resp = users[respId];
            if (resp) {
              responsabili.push({
                id: respId,
                nome: resp.displayName,
                email: resp.email
              });
            }
          });
        }
        
        return {
          ...stats[locationId],
          locationId,
          locationName,
          responsabili,  // ✅ Aggiungi responsabili
          personaleCount: stats[locationId].personalePresente.size,
          personalePresente: Array.from(stats[locationId].personalePresente)
        };
      })
    );
    
    // Ordina per numero di personale (decrescente)
    statsArray.sort((a, b) => b.personaleCount - a.personaleCount);
    
    setCantieriStats(statsArray);
  };

  // ========================================
  // VERIFICA ANOMALIE PER UN CANTIERE
  // ========================================
  
  const getPersonaleDetails = async (locationId) => {
    const details = [];
    
    // Trova il cantiere/location
    const locationStats = cantieriStats.find(s => s.locationId === locationId);
    if (!locationStats) return [];
    
    // Per ogni persona presente
    for (const userId of locationStats.personalePresente) {
      const user = users[userId];
      if (!user) continue;
      
      // ✅ Verifica timbratura (badge o manuale)
      // NOTA: Le timbrature badge NON hanno clockInLocation
      //       Hanno scanInfo.clockIn.deviceId invece
      const hasTimbratura = timbrature.some(t => {
        // Match userId
        const tUserId = t.userId || t.personaId || t.id || t.uid;
        if (tUserId !== userId) return false;
        
        // ✅ Timbratura trovata per questo utente
        // Non verifichiamo location perché timbrature badge non hanno clockInLocation
        // La posizione viene determinata dal deviceId del kiosk
        return true;
      });
      
      // ✅ SOLUZIONE 2: Verifica rapportino con 3 stati
      // 1. Rapportino stesso cantiere
      const rapportinoStessoCantiere = rapportini.find(r => {
        if (r.cantiereId !== locationId) return false;
        
        if (!r.personalePresente || !Array.isArray(r.personalePresente)) {
          return false;
        }
        
        return r.personalePresente.some(p => {
          const personaId = p.personaId || p.userId || p.id || p.uid;
          return personaId === userId;
        });
      });
      
      // 2. Rapportino altro cantiere
      const rapportinoAltroCantiere = rapportini.find(r => {
        if (r.cantiereId === locationId) return false;
        
        if (!r.personalePresente || !Array.isArray(r.personalePresente)) {
          return false;
        }
        
        return r.personalePresente.some(p => {
          const personaId = p.personaId || p.userId || p.id || p.uid;
          return personaId === userId;
        });
      });
      
      // ✅ Verifica ore manuali vs badge in workHours
      // IMPORTANTE: workHours può contenere:
      // - Ore con lastScanner → inserite da timbratura badge
      // - Ore senza lastScanner → inserite manualmente
      
      const userWorkHours = workHours.filter(wh => {
        const whUserId = wh.userId || wh.personaId || wh.id || wh.uid;
        return whUserId === userId;
      });
      
      // workHours CON lastScanner = da badge (equivalente a timbratura)
      const hasWorkHoursWithBadge = userWorkHours.some(wh => wh.lastScanner);
      
      // workHours SENZA lastScanner = manuali
      const hasManualHours = userWorkHours.some(wh => !wh.lastScanner);
      
      // ✅ Considera timbratura presente se:
      // 1. C'è documento in collection timekeeping, OPPURE
      // 2. C'è workHours con lastScanner (da badge)
      const hasTimbraturaCompleta = hasTimbratura || hasWorkHoursWithBadge;
      
      // Determina stato rapportino
      let rapportinoStatus = 'missing';  // missing, ok, different
      let rapportinoCantiereName = null;
      
      if (rapportinoStessoCantiere) {
        rapportinoStatus = 'ok';
      } else if (rapportinoAltroCantiere) {
        rapportinoStatus = 'different';
        // Recupera nome cantiere diverso
        try {
          rapportinoCantiereName = await getLocationName(rapportinoAltroCantiere.cantiereId);
        } catch (error) {
          rapportinoCantiereName = rapportinoAltroCantiere.cantiereId;
        }
      }
      
      // ✅ Determina anomalie con logica migliorata
      const anomalie = [];
      
      // CASO 1: Persona nel rapportino di questo cantiere
      if (rapportinoStatus === 'ok') {
        // Ma senza ore registrate (né timbratura né manuali)
        if (!hasTimbraturaCompleta && !hasManualHours) {
          anomalie.push('In rapportino senza ore registrate');
        }
      } 
      // CASO 2: Persona nel rapportino di un altro cantiere
      else if (rapportinoStatus === 'different') {
        anomalie.push(`Rapportino in: ${rapportinoCantiereName}`);
        // E se non ha nemmeno timbratura qui
        if (!hasTimbraturaCompleta && !hasManualHours) {
          anomalie.push('Nessuna ora registrata qui');
        }
      }
      // CASO 3: Persona non nel rapportino da nessuna parte
      else if (rapportinoStatus === 'missing') {
        if (!hasTimbraturaCompleta && !hasManualHours) {
          anomalie.push('Manca timbratura e ore manuali');
        } else if (!hasTimbraturaCompleta) {
          anomalie.push('Manca timbratura');
        }
        anomalie.push('Non in rapportino');
      }
      
      details.push({
        userId,
        user,
        hasTimbratura: hasTimbraturaCompleta,  // ✅ Usa versione completa
        hasManualHours,
        rapportinoStatus,
        rapportinoCantiereName,
        anomalie,
        hasAnomalie: anomalie.length > 0
      });
    }
    
    // Ordina: prima le anomalie, poi alfabetico
    details.sort((a, b) => {
      if (a.hasAnomalie && !b.hasAnomalie) return -1;
      if (!a.hasAnomalie && b.hasAnomalie) return 1;
      return a.user.displayName.localeCompare(b.user.displayName);
    });
    
    return details;
  };

  // ========================================
  // GESTIONE CALENDARIO
  // ========================================
  
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getWeekDates = () => {
    const startOfWeek = new Date(selectedDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedCantiere(null);
  };

  const handleDateInputChange = (e) => {
    const newDate = new Date(e.target.value);
    if (!isNaN(newDate.getTime())) {
      setSelectedDate(newDate);
    }
  };

  // ========================================
  // GESTIONE SELEZIONE CANTIERE
  // ========================================
  
  const handleCantiereClick = async (locationId) => {
    if (locationId === selectedCantiere) {
      // Chiudi se già selezionato
      setSelectedCantiere(null);
      setPersonaleDetails([]);
    } else {
      // Apri e carica dettagli
      setSelectedCantiere(locationId);
      const details = await getPersonaleDetails(locationId);
      setPersonaleDetails(details);
    }
  };

  // ========================================
  // UTILITY
  // ========================================
  
  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const isToday = (date) => {
    const today = new Date();
    return formatDate(date) === formatDate(today);
  };

  const isSameDate = (date1, date2) => {
    return formatDate(date1) === formatDate(date2);
  };

  // ========================================
  // 🔍 FUNZIONI RICERCA PERSONA
  // ========================================
  
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Reset selezione cantiere quando si cerca
    if (query.trim()) {
      setSelectedCantiere(null);
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    setSelectedCantiere(null);
  };
  
  // Filtra cantieri in base alla ricerca
  const getFilteredCantieriStats = () => {
    if (!searchQuery.trim()) {
      return cantieriStats;
    }
    
    const query = searchQuery.toLowerCase().trim();
    
    return cantieriStats.filter(stat => {
      // ✅ Verifica che personalePresente esista e sia un array
      if (!stat.personalePresente || !Array.isArray(stat.personalePresente)) {
        return false;
      }
      
      // Cerca tra tutti gli utenti del cantiere
      return stat.personalePresente.some(userId => {
        const user = users[userId];
        if (!user) return false;
        
        const fullName = user.displayName.toLowerCase();
        const nome = (user.nome || '').toLowerCase();
        const cognome = (user.cognome || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        
        return fullName.includes(query) || 
               nome.includes(query) || 
               cognome.includes(query) || 
               email.includes(query);
      });
    });
  };
  
  // Verifica se una persona è presente in un cantiere
  const isPersonInCantiere = (stat) => {
    if (!searchQuery.trim()) return false;
    
    // ✅ Verifica che personalePresente esista e sia un array
    if (!stat.personalePresente || !Array.isArray(stat.personalePresente)) {
      return false;
    }
    
    const query = searchQuery.toLowerCase().trim();
    
    return stat.personalePresente.some(userId => {
      const user = users[userId];
      if (!user) return false;
      
      const fullName = user.displayName.toLowerCase();
      const nome = (user.nome || '').toLowerCase();
      const cognome = (user.cognome || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      
      return fullName.includes(query) || 
             nome.includes(query) || 
             cognome.includes(query) || 
             email.includes(query);
    });
  };
  
  // Ottieni i nomi delle persone trovate in un cantiere
  const getMatchingPersonsInCantiere = (stat) => {
    if (!searchQuery.trim()) return [];
    
    // ✅ Verifica che personalePresente esista e sia un array
    if (!stat.personalePresente || !Array.isArray(stat.personalePresente)) {
      return [];
    }
    
    const query = searchQuery.toLowerCase().trim();
    
    return stat.personalePresente
      .filter(userId => {
        const user = users[userId];
        if (!user) return false;
        
        const fullName = user.displayName.toLowerCase();
        const nome = (user.nome || '').toLowerCase();
        const cognome = (user.cognome || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        
        return fullName.includes(query) || 
               nome.includes(query) || 
               cognome.includes(query) || 
               email.includes(query);
      })
      .map(userId => users[userId].displayName);
  };

  // ========================================
  // RENDER
  // ========================================
  
  return (
    <div className="verifica-personale-container">
      {/* HEADER */}
      <div className="verifica-header">
        <h2>📍 Verifica Posizione Personale</h2>
        <p>Controlla le timbrature e la presenza del personale nei cantieri</p>
      </div>

      {/* CALENDARIO */}
      <div className="calendario-section">
        <div className="calendario-controls">
          <button className="btn-nav" onClick={goToPreviousWeek}>
            ← Settimana Precedente
          </button>
          
          <div className="date-selector">
            <input
              type="date"
              value={formatDate(selectedDate)}
              onChange={handleDateInputChange}
              className="date-input"
            />
            <button className="btn-today" onClick={goToToday}>
              Oggi
            </button>
          </div>
          
          <button className="btn-nav" onClick={goToNextWeek}>
            Settimana Successiva →
          </button>
        </div>

        <div className="week-calendar">
          {getWeekDates().map((date, index) => (
            <div
              key={index}
              className={`day-card ${isSameDate(date, selectedDate) ? 'selected' : ''} ${isToday(date) ? 'today' : ''}`}
              onClick={() => handleDateSelect(date)}
            >
              <div className="day-name">
                {date.toLocaleDateString('it-IT', { weekday: 'short' })}
              </div>
              <div className="day-number">
                {date.getDate()}
              </div>
              <div className="day-month">
                {date.toLocaleDateString('it-IT', { month: 'short' })}
              </div>
            </div>
          ))}
        </div>

        <div className="selected-date-display">
          <strong>Data selezionata:</strong> {formatDisplayDate(selectedDate)}
        </div>
      </div>

      {/* LOADING */}
      {isLoading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Caricamento dati...</p>
        </div>
      )}

      {/* 🔍 RICERCA PERSONA */}
      {!isLoading && cantieriStats.length > 0 && (
        <div className="search-section">
          <div className="search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Cerca persona per nome, cognome o email..."
                value={searchQuery}
                onChange={handleSearchChange}
                aria-label="Cerca persona"
              />
              {searchQuery && (
                <button 
                  className="search-clear"
                  onClick={clearSearch}
                  aria-label="Cancella ricerca"
                >
                  ✕
                </button>
              )}
            </div>
            
            {searchQuery && (
              <div className="search-results-info">
                {getFilteredCantieriStats().length > 0 ? (
                  <span className="search-results-count">
                    ✓ Trovato in {getFilteredCantieriStats().length} {getFilteredCantieriStats().length === 1 ? 'cantiere' : 'cantieri'}
                  </span>
                ) : (
                  <span className="search-results-empty">
                    ⚠️ Nessuna persona trovata con "{searchQuery}"
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* STATISTICHE CANTIERI */}
      {!isLoading && cantieriStats.length === 0 && (
        <div className="no-data">
          <p>📭 Nessun personale presente per questa data</p>
        </div>
      )}

      {!isLoading && cantieriStats.length > 0 && (
        <div className="cantieri-stats-section">
          <h3>
            {searchQuery ? 'Risultati Ricerca' : 'Cantieri con Personale Presente'}
          </h3>
          <p className="subtitle">
            {searchQuery 
              ? `Cantieri dove è presente "${searchQuery}"`
              : 'Clicca su un cantiere per vedere il dettaglio'}
          </p>
          
          <div className="cantieri-cards">
            {getFilteredCantieriStats().map(stat => {
              const cantiere = cantieri.find(c => c.id === stat.locationId);
              const isHighlighted = searchQuery && isPersonInCantiere(stat);
              const matchingPersons = searchQuery ? getMatchingPersonsInCantiere(stat) : [];
              
              return (
                <div
                  key={stat.locationId}
                  className={`cantiere-card ${selectedCantiere === stat.locationId ? 'active' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                  onClick={() => handleCantiereClick(stat.locationId)}
                >
                  <div className="cantiere-name">
                    {cantiere?.isOfficina ? '🔧' : '🏗️'} {stat.locationName}
                  </div>
                  
                  {/* 🔍 Persone trovate dalla ricerca */}
                  {searchQuery && matchingPersons.length > 0 && (
                    <div className="search-match-persons">
                      <span className="search-match-label">🔍 Trovato:</span>
                      {matchingPersons.map((personName, idx) => (
                        <span key={idx} className="search-match-name">
                          {personName}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* ✅ Responsabili */}
                  {stat.responsabili && stat.responsabili.length > 0 && (
                    <div className="cantiere-responsabili">
                      <span className="responsabili-label">👤 Responsabile:</span>
                      {stat.responsabili.map((resp, idx) => (
                        <span key={resp.id} className="responsabile-name">
                          {resp.nome}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="cantiere-count">
                    <span className="count-badge">{stat.personaleCount}</span>
                    <span className="count-label">
                      {stat.personaleCount === 1 ? 'persona' : 'persone'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DETTAGLIO CANTIERE SELEZIONATO */}
      {selectedCantiere && (
        <div className="cantiere-detail-section">
          <div className="detail-header">
            <h3>
              Dettaglio Personale - {cantieriStats.find(s => s.locationId === selectedCantiere)?.locationName}
            </h3>
            <button 
              className="btn-close-detail" 
              onClick={() => setSelectedCantiere(null)}
            >
              ✕ Chiudi
            </button>
          </div>

          <div className="personale-table-container">
            <table className="personale-table">
              <thead>
                <tr>
                  <th>Dipendente</th>
                  <th>Timbratura</th>
                  <th>Rapportino</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {personaleDetails.map(detail => (
                  <tr 
                    key={detail.userId}
                    className={detail.hasAnomalie ? 'anomalia-row' : ''}
                  >
                    <td className="user-cell" data-label="Dipendente">
                      <div className="user-name">{detail.user.displayName}</div>
                      <div className="user-email">{detail.user.email}</div>
                    </td>
                    
                    {/* ✅ TIMBRATURA con badge Manuale */}
                    <td className="status-cell" data-label="Timbratura">
                      {detail.hasTimbratura ? (
                        <span className="status-badge success">✓ Presente</span>
                      ) : detail.hasManualHours ? (
                        <span className="status-badge manual">⚙️ Manuale</span>
                      ) : (
                        <span className="status-badge error">✗ Mancante</span>
                      )}
                    </td>
                    
                    {/* ✅ RAPPORTINO con 3 stati (ok, different, missing) */}
                    <td className="status-cell" data-label="Rapportino">
                      {detail.rapportinoStatus === 'ok' ? (
                        <span className="status-badge success">✓ Presente</span>
                      ) : detail.rapportinoStatus === 'different' ? (
                        <span className="status-badge warning">
                          ⚠️ In: {detail.rapportinoCantiereName}
                        </span>
                      ) : (
                        <span className="status-badge error">✗ Mancante</span>
                      )}
                    </td>
                    
                    {/* ✅ ANOMALIE */}
                    <td className="anomalie-cell" data-label="Stato">
                      {detail.hasAnomalie ? (
                        <div className="anomalie-list">
                          {detail.anomalie.map((anomalia, idx) => (
                            <span 
                              key={idx} 
                              className={`anomalia-tag ${anomalia.includes('Rapportino in:') ? 'warning' : 'error'}`}
                            >
                              {anomalia.includes('Rapportino in:') ? '⚠️' : '❌'} {anomalia}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="status-badge ok">✓ Tutto OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* NOTIFICHE */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default VerificaPersonale;